import { TWIN_WORKFLOW_OPERATIONS } from "./twin-workflows.js";
import { z } from "zod";
import { externalClientAppSchema } from "./client-attribution.js";
import { CHANNEL_OBJECT_FIELD_TYPE_CATALOG, channelObjectFieldTypeDefinitionSchema, } from "./channel-field-types.js";
import { channelGroupIdSchema, channelIdSchema, channelDataViewTypeSchema, matrixValueFormatSchema, channelObjectFieldTypeSchema, channelRecordFilterOperatorSchema, channelRecordFilterSchema, channelObjectTypeIdSchema, channelSlugSchema, channelStatusSchema, channelViewIdSchema, channelViewTypeSchema, channelVisibilitySchema, createChannelInputSchema, createChannelObjectFieldInputSchema, createChannelObjectTypeInputSchema, createChannelRecordInputSchema, createChannelViewInputSchema, } from "./channels.js";
import { runIdSchema, sessionRoutingArchitectureSchema, taskIdSchema, taskRunStatusSchema, } from "./primitives.js";
import { twinSessionSummarySchema } from "./twin-public-sessions.js";
import { artifactKindSchema, channelArtifactOriginSchema, artifactProviderSchema, artifactVersionStatusSchema, } from "./artifacts.js";
import { twinExecutionCertificateRefSchema } from "./twin-execution-certificates.js";
import { channelDataFreshnessSchema } from "./channel-data-trace.js";
const identifierSchema = z.string().regex(/^[a-z][a-z0-9_]{0,79}$/u);
const fieldKeySchema = z.string().trim().min(1).max(120)
    .regex(/^[A-Za-z_][A-Za-z0-9_]*$/u);
const scalarSchema = z.union([z.string().max(2_000), z.number().finite(), z.boolean()]);
const twinProductSlugSchema = z.string().trim().min(1).max(120)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/u);
export const twinQueryInterfaceSchema = z.enum(["api", "cli", "mcp"]);
export const twinDeterministicFilterSchema = z.discriminatedUnion("operator", [
    z.object({ field: fieldKeySchema, operator: z.enum(["eq", "not_eq", "gt", "gte", "lt", "lte", "contains", "not_contains", "starts_with", "ends_with"]), value: scalarSchema }).strict(),
    z.object({ field: fieldKeySchema, operator: z.enum(["in", "not_in"]), values: z.array(scalarSchema).min(1).max(50) }).strict(),
    z.object({ field: fieldKeySchema, operator: z.enum(["between", "not_between"]), values: z.array(scalarSchema).length(2) }).strict(),
    z.object({ field: fieldKeySchema, operator: z.enum(["is_null", "is_not_null", "is_blank", "is_not_blank"]) }).strict(),
]);
const orderBySchema = z.object({
    field: fieldKeySchema,
    direction: z.enum(["asc", "desc"]),
}).strict();
const metricSchema = z.object({
    alias: identifierSchema,
    function: z.enum(["count_rows", "count_distinct", "sum", "average", "minimum", "maximum"]),
    field: fieldKeySchema.optional(),
}).strict().superRefine((metric, context) => {
    if (metric.function === "count_rows" && metric.field) {
        context.addIssue({ code: "custom", path: ["field"], message: "count_rows does not accept a field" });
    }
    if (metric.function !== "count_rows" && !metric.field) {
        context.addIssue({ code: "custom", path: ["field"], message: `${metric.function} requires a field` });
    }
});
const deterministicBase = {
    mode: z.literal("deterministic"),
    interfaceType: twinQueryInterfaceSchema.default("api"),
    clientApp: externalClientAppSchema.optional(),
    channelId: channelIdSchema,
    objectTypeId: z.string().trim().min(1).max(200),
    filters: z.array(twinDeterministicFilterSchema).max(16).default([]),
    orderBy: z.array(orderBySchema).max(4).default([]),
    limit: z.number().int().min(1).max(200).default(20),
};
export const twinSessionQueryInputSchema = z.object({
    mode: z.literal("session"),
    interfaceType: twinQueryInterfaceSchema.default("api"),
    clientApp: externalClientAppSchema.optional(),
    question: z.string().trim().min(1).max(8_000),
    channelId: channelIdSchema.optional(),
    viewId: channelViewIdSchema.optional(),
    routingArchitecture: sessionRoutingArchitectureSchema.optional(),
}).strict().superRefine((input, context) => {
    if (input.viewId && !input.channelId) {
        context.addIssue({ code: "custom", path: ["viewId"], message: "viewId requires channelId" });
    }
});
export const twinDeterministicRecordsInputSchema = z.object({
    ...deterministicBase,
    operation: z.literal("records"),
    fields: z.array(fieldKeySchema).min(1).max(20),
    cursor: z.string().trim().min(1).max(4_096).optional(),
}).strict();
export const twinDeterministicAggregateInputSchema = z.object({
    ...deterministicBase,
    operation: z.literal("aggregate"),
    groupBy: z.array(fieldKeySchema).max(8).default([]),
    metrics: z.array(metricSchema).min(1).max(12),
    cursor: z.string().trim().min(1).max(4_096).optional(),
}).strict();
export const twinDeterministicViewInputSchema = z.object({
    mode: z.literal("deterministic"),
    interfaceType: twinQueryInterfaceSchema.default("api"),
    clientApp: externalClientAppSchema.optional(),
    operation: z.literal("view"),
    channelId: channelIdSchema,
    viewId: channelViewIdSchema,
    widgetIds: z.array(z.string().trim().min(1).max(120)).min(1).max(24)
        .transform((ids) => [...new Set(ids)]).optional(),
}).strict();
export const twinQueryCreateInputSchema = z.union([
    twinSessionQueryInputSchema,
    twinDeterministicRecordsInputSchema,
    twinDeterministicAggregateInputSchema,
    twinDeterministicViewInputSchema,
]);
/*
 * Flat MCP-facing input schema for the twin_query tool. The authoritative
 * shape is the discriminated `twinQueryCreateInputSchema` union above, but a
 * Zod union has no `.shape`, so the MCP SDK serialises it to an empty
 * `{properties:{}}` JSON Schema and no arguments ever reach the server. This
 * flat object — every field optional except the `mode` discriminator, reusing
 * the same sub-schemas so it cannot drift — serialises to real properties;
 * the service re-parses against the union for authoritative validation, so
 * invalid mode/operation combinations are still rejected there. `interfaceType`
 * is intentionally omitted: the gateway injects it.
 */
export const twinQueryMcpInputSchema = z.object({
    mode: z.enum(["session", "deterministic"])
        .describe("'session' for a routed natural-language question; 'deterministic' for a typed channel query."),
    operation: z.enum(["records", "aggregate", "view"]).optional()
        .describe("Deterministic mode only: records | aggregate | view."),
    clientApp: externalClientAppSchema.optional(),
    channelId: channelIdSchema.optional(),
    objectTypeId: z.string().trim().min(1).max(200).optional()
        .describe("Deterministic records/aggregate: the object type to query."),
    question: z.string().trim().min(1).max(8_000).optional()
        .describe("Session mode: the natural-language question."),
    viewId: channelViewIdSchema.optional()
        .describe("Session mode optional focus, or deterministic view operation target."),
    fields: z.array(fieldKeySchema).max(20).optional()
        .describe("Deterministic records: field keys to return."),
    cursor: z.string().trim().min(1).max(4_096).optional()
        .describe("Deterministic records or grouped aggregate: continue from nextCursor with the same query shape."),
    filters: z.array(twinDeterministicFilterSchema).max(16).optional(),
    orderBy: z.array(orderBySchema).max(4).optional(),
    limit: z.number().int().min(1).max(200).optional(),
    groupBy: z.array(fieldKeySchema).max(8).optional()
        .describe("Deterministic aggregate: group-by field keys."),
    metrics: z.array(metricSchema).max(12).optional()
        .describe("Deterministic aggregate: metric definitions."),
    widgetIds: z.array(z.string().trim().min(1).max(120)).max(24).optional()
        .describe("Deterministic view: widget IDs to materialise."),
});
export const twinPublicChannelSchema = z.object({
    id: channelIdSchema,
    slug: z.string().trim().min(1),
    name: z.string().trim().min(1),
    description: z.string(),
    purpose: z.string(),
    visibility: channelVisibilitySchema,
    status: channelStatusSchema,
    recordCount: z.number().int().nonnegative().optional(),
    objectTypeNames: z.array(z.string()).optional(),
}).strict();
export const twinPublicChannelObjectTypeSchema = z.object({
    id: z.string().trim().min(1),
    slug: z.string().trim().min(1),
    name: z.string().trim().min(1),
    description: z.string(),
    status: channelStatusSchema,
}).strict();
export const twinPublicChannelFieldSchema = z.object({
    id: z.string().trim().min(1),
    objectTypeId: z.string().trim().min(1),
    key: z.string().trim().min(1),
    label: z.string().trim().min(1),
    type: channelObjectFieldTypeSchema,
    required: z.boolean(),
    // Compatibility window: mixed-version tenant control planes may not return
    // presentation metadata until their next fleet rollout.
    position: z.number().int().nonnegative().optional(),
    config: z.record(z.string(), z.unknown()).optional(),
    options: z.array(z.string()).optional(),
    referenceObjectTypeId: z.string().trim().min(1).optional(),
    valueSemantics: z.enum([
        "organisation_user_id",
        "organisation_user_id_array",
        "channel_record_id",
        "configured_option",
        "configured_option_array",
    ]).optional(),
    filterOperators: z.array(channelRecordFilterOperatorSchema).optional(),
    status: channelStatusSchema,
}).strict();
export const twinPublicChannelViewSummarySchema = z.object({
    id: channelViewIdSchema,
    slug: z.string().trim().min(1),
    name: z.string().trim().min(1),
    type: channelViewTypeSchema,
    status: channelStatusSchema,
    objectTypeId: z.string().trim().min(1).optional(),
    currentVersionId: z.string().trim().min(1).optional(),
    recordFilters: z.array(channelRecordFilterSchema).max(12).optional(),
}).strict();
export const twinPublicChannelViewSchema = twinPublicChannelViewSummarySchema.extend({
    currentDefinition: z.record(z.string(), z.unknown()).optional(),
}).strict();
export const twinChannelsListResponseSchema = z.object({
    channels: z.array(twinPublicChannelSchema),
}).strict();
/**
 * A named section of the organisation's channel navigation, as the app's
 * sidebar shows it: its place among the groups and the active channels filed
 * in it that the reader may open, by ID. A group grants no access; a channel
 * in no group is in none of these lists.
 */
export const twinChannelGroupSchema = z.object({
    id: channelGroupIdSchema,
    name: z.string().trim().min(1),
    position: z.number().int().nonnegative(),
    channelIds: z.array(channelIdSchema),
}).strict();
export const twinChannelGroupsListResponseSchema = z.object({
    groups: z.array(twinChannelGroupSchema),
}).strict();
export const twinChannelInspectResponseSchema = z.object({
    channel: twinPublicChannelSchema,
    // Optional only for the explicit mixed-version rollout window. Current
    // control planes always return the complete canonical catalog.
    fieldTypes: z.array(channelObjectFieldTypeDefinitionSchema)
        .length(CHANNEL_OBJECT_FIELD_TYPE_CATALOG.length)
        .optional(),
    recordValueContext: z.object({
        currentUserId: z.string().trim().min(1),
        userFieldValues: z.literal("organisation_user_id"),
    }).strict().optional(),
    recordFilterContext: z.object({
        maxFilters: z.literal(12),
        combination: z.literal("and"),
        queryParameter: z.literal("filters"),
    }).strict().optional(),
    objectTypes: z.array(z.object({
        objectType: twinPublicChannelObjectTypeSchema,
        fields: z.array(twinPublicChannelFieldSchema),
    }).strict()),
}).strict();
export const twinChannelCreateInputSchema = createChannelInputSchema
    .omit({ organisationId: true, metadata: true })
    .strict();
export const twinChannelCreateResponseSchema = z.object({
    channel: twinPublicChannelSchema,
}).strict();
export const twinChannelUpdateInputSchema = z.object({
    name: z.string().trim().min(1).max(200).optional(),
    description: z.string().max(5_000).optional(),
    status: channelStatusSchema.optional(),
}).strict().refine((input) => input.name !== undefined || input.description !== undefined
    || input.status !== undefined, { message: "At least one of name, description, or status is required" });
export const twinChannelUpdateResponseSchema = twinChannelCreateResponseSchema;
export const twinChannelObjectTypeCreateInputSchema = createChannelObjectTypeInputSchema
    .omit({ channelId: true, metadata: true })
    .strict();
export const twinChannelObjectTypeCreateResponseSchema = z.object({
    objectType: twinPublicChannelObjectTypeSchema,
}).strict();
export const twinChannelObjectTypeUpdateInputSchema = z.object({
    name: z.string().trim().min(1).max(200).optional(),
    description: z.string().max(5_000).optional(),
    status: channelStatusSchema.optional(),
}).strict().refine((input) => input.name !== undefined || input.description !== undefined
    || input.status !== undefined, {
    message: "At least one of name, description, or status is required",
});
export const twinChannelObjectTypeUpdateResponseSchema = twinChannelObjectTypeCreateResponseSchema;
export const twinChannelObjectFieldCreateInputSchema = createChannelObjectFieldInputSchema
    .omit({ channelId: true, objectTypeId: true })
    .strict();
export const twinChannelObjectFieldCreateResponseSchema = z.object({
    field: twinPublicChannelFieldSchema,
}).strict();
export const twinChannelObjectFieldUpdateInputSchema = z.object({
    label: z.string().trim().min(1).max(200).optional(),
    required: z.boolean().optional(),
    position: z.number().int().min(0).max(500).optional(),
    config: z.record(z.string(), z.unknown()).optional(),
    status: channelStatusSchema.optional(),
}).strict().refine((input) => input.label !== undefined || input.required !== undefined
    || input.position !== undefined || input.config !== undefined || input.status !== undefined, {
    message: "At least one of label, required, position, config, or status is required",
});
export const twinChannelObjectFieldUpdateResponseSchema = twinChannelObjectFieldCreateResponseSchema;
export const twinChannelViewCreateInputSchema = z.object({
    objectTypeId: channelObjectTypeIdSchema.optional(),
    slug: channelSlugSchema.optional(),
    name: z.string().trim().min(1).max(200),
    type: channelDataViewTypeSchema.default("table"),
    config: z.record(z.string(), z.unknown()).default({}),
    definition: z.record(z.string(), z.unknown()).default({}),
    makeDefault: z.boolean().default(false),
}).strict().superRefine((input, context) => {
    const result = createChannelViewInputSchema.safeParse({
        channelId: "public-channel-validation",
        ...input,
    });
    if (result.success)
        return;
    for (const issue of result.error.issues) {
        context.addIssue({ ...issue, path: issue.path });
    }
});
export const twinChannelViewCreateResponseSchema = z.object({
    view: twinPublicChannelViewSchema,
}).strict();
export const twinChannelViewsListResponseSchema = z.object({
    views: z.array(twinPublicChannelViewSummarySchema),
}).strict();
export const twinChannelMemberSchema = z.object({
    userId: z.string().trim().min(1).max(200),
    role: z.enum(["owner", "member"]),
}).strict();
export const twinChannelMembersListResponseSchema = z.object({
    members: z.array(twinChannelMemberSchema),
}).strict();
// userId is the canonical organisation user ID that record user fields, feed
// authors and actors, and session owners carry. Email is deliberately absent.
// The bounds are the identity directory's own, so a member it can hold is one
// this list can return.
export const twinMemberSchema = z.object({
    userId: z.string().trim().min(1).max(240),
    externalUserId: z.string().trim().min(1).max(240).optional(),
    displayName: z.string().trim().min(1).max(240).optional(),
    isCurrentUser: z.boolean(),
}).strict();
export const twinMembersListResponseSchema = z.object({
    members: z.array(twinMemberSchema),
}).strict();
export const twinChannelViewResponseSchema = z.object({
    view: twinPublicChannelViewSchema,
}).strict();
export const twinChannelRecordSchema = z.object({
    id: z.string().trim().min(1),
    objectTypeId: z.string().trim().min(1),
    title: z.string(),
    values: z.record(z.string(), z.unknown()),
    status: channelStatusSchema,
    version: z.number().int().positive(),
}).strict();
export const twinChannelViewDataOptionsSchema = z.object({
    filters: z.array(channelRecordFilterSchema).max(12).optional(),
    fields: z.array(z.string().trim().min(1)).max(20).optional(),
    limit: z.number().int().min(1).max(200).optional(),
    cursor: z.string().trim().min(1).max(4_096).optional(),
}).strict();
export const twinChannelDashboardDataResponseSchema = z.object({
    channelId: channelIdSchema,
    viewId: channelViewIdSchema,
    generatedAt: z.number().int().nonnegative(),
    freshness: channelDataFreshnessSchema,
    widgets: z.record(z.string().trim().min(1), z.record(z.string(), z.unknown())),
}).strict();
const twinChannelMatrixRowSchema = z.object({
    /** Full grouping path, including ancestors hidden by indentation; empty for closing totals. */
    rowPath: z.array(z.string()).max(3).optional(),
    key: z.string().trim().min(1),
    labels: z.array(z.string()),
    labelFields: z.array(z.string().nullable()).optional(),
    heading: z.boolean().optional(),
    measureId: z.string().trim().min(1).optional(),
    fieldKey: z.string().trim().min(1).optional(),
    fieldType: z.string().trim().min(1),
    format: matrixValueFormatSchema,
    emphasis: z.enum(["normal", "subtotal", "total"]),
    level: z.number().int().min(0).max(3),
    values: z.record(z.string(), z.number().finite().nullable()),
    total: z.number().finite().nullable(),
}).strict();
export const twinChannelMatrixDataResponseSchema = z.object({
    channelId: channelIdSchema,
    viewId: channelViewIdSchema,
    generatedAt: z.number().int().nonnegative(),
    freshness: channelDataFreshnessSchema,
    columns: z.array(z.object({
        key: z.string().trim().min(1),
        label: z.string().trim().min(1),
        sortValue: z.string(),
    }).strict()).max(120),
    rows: z.array(twinChannelMatrixRowSchema).max(500),
    totalRows: z.array(twinChannelMatrixRowSchema).max(51).optional(),
    columnTotals: z.record(z.string(), z.number().finite().nullable()),
}).strict();
export const twinChannelRecordReferenceSchema = z.object({
    id: z.string().trim().min(1),
    objectTypeId: channelObjectTypeIdSchema,
    channelId: channelIdSchema,
    title: z.string(),
    status: channelStatusSchema,
}).strict();
export const twinChannelRecordViewDataResponseSchema = z.object({
    channelId: channelIdSchema,
    viewId: channelViewIdSchema,
    objectTypeId: channelObjectTypeIdSchema,
    viewType: z.enum(["table", "board", "calendar", "timeline"]),
    generatedAt: z.number().int().nonnegative(),
    freshness: channelDataFreshnessSchema,
    fieldKeys: z.array(z.string().trim().min(1)).min(1).max(22),
    // The view's further fields beyond the 22 one page carries, in its order;
    // request them with `fields`. Absent when the view fits.
    omittedFieldKeys: z.array(z.string().trim().min(1)).min(1).max(1_000).optional(),
    records: z.array(twinChannelRecordSchema).max(200),
    references: z.array(twinChannelRecordReferenceSchema).max(4_000),
    hasMore: z.boolean(),
    nextCursor: z.string().trim().min(1).nullable(),
    // Present when the page ends the result set, so an empty Data Set is
    // distinguishable from a filtered-out page.
    totalCount: z.number().int().nonnegative().optional(),
}).strict();
export const twinChannelViewDataResponseSchema = z.union([
    twinChannelDashboardDataResponseSchema,
    twinChannelMatrixDataResponseSchema,
    twinChannelRecordViewDataResponseSchema,
]);
export const twinChannelRecordCreateInputSchema = createChannelRecordInputSchema
    .omit({ channelId: true, sourceSessionId: true, sourceRunId: true })
    .extend({
    idempotencyKey: z.string().trim().min(8).max(160)
        .regex(/^[A-Za-z0-9][A-Za-z0-9._:-]*$/u),
})
    .strict();
export const twinChannelRecordCreateResponseSchema = z.object({
    record: twinChannelRecordSchema,
    receipt: z.object({
        id: z.string().trim().min(1),
        idempotencyKey: z.string().trim().min(8).max(160),
        eventId: z.string().trim().min(1),
        replayed: z.boolean(),
    }).strict(),
}).strict();
export const twinChannelRecordResponseSchema = z.object({
    record: twinChannelRecordSchema,
}).strict();
export const twinChannelRecordUpdateInputSchema = z.object({
    expectedVersion: z.number().int().positive(),
    title: z.string().max(500).optional(),
    values: z.record(z.string(), z.unknown()).optional(),
    status: channelStatusSchema.optional(),
}).strict().refine((input) => input.title !== undefined || input.values !== undefined || input.status !== undefined, { message: "At least one of title, values, or status is required" });
export const twinChannelViewUpdateInputSchema = z.object({
    expectedCurrentVersionId: z.string().trim().min(1).optional(),
    name: z.string().trim().min(1).max(200).optional(),
    status: channelStatusSchema.optional(),
    definition: z.record(z.string(), z.unknown()).optional(),
}).strict().superRefine((input, ctx) => {
    if (input.name === undefined && input.status === undefined && input.definition === undefined) {
        ctx.addIssue({
            code: "custom",
            message: "At least one of name, status, or definition is required",
        });
    }
    if (input.definition !== undefined && input.expectedCurrentVersionId === undefined) {
        ctx.addIssue({
            code: "custom",
            path: ["expectedCurrentVersionId"],
            message: "expectedCurrentVersionId is required when publishing a definition",
        });
    }
});
export const twinChannelViewUpdateResponseSchema = twinChannelViewResponseSchema;
// Product tests remain an internal operator contract. Public queries no longer
// ask callers to discover or select products.
export const twinProductQueryCreateInputSchema = z.object({
    question: z.string().trim().min(1).max(8_000),
    productSlug: twinProductSlugSchema,
    productVersion: z.number().int().positive().optional(),
}).strict();
export const twinQueryProductSchema = z.object({
    slug: twinProductSlugSchema,
    version: z.number().int().positive(),
    publicationVersionId: z.string().trim().min(1).max(200),
    relation: z.string().trim().min(1).max(260),
}).strict();
export function safeTwinNavigationUrl(value) {
    try {
        const url = new URL(value);
        const loopback = ["localhost", "127.0.0.1", "[::1]"].includes(url.hostname);
        return !url.username && !url.password && !url.hash &&
            (url.protocol === "https:" || (url.protocol === "http:" && loopback)) ? url : null;
    }
    catch {
        return null;
    }
}
const twinNavigationHrefSchema = z.string().url().max(4_096)
    .refine((value) => safeTwinNavigationUrl(value) !== null, "Twin navigation links must use HTTPS, except for loopback development URLs.");
export const twinQueryChannelResourceSchema = z.object({
    type: z.literal("channel"), id: channelIdSchema, title: z.string().trim().min(1).max(200),
    relationship: z.literal("product_view"), href: twinNavigationHrefSchema,
    viewId: channelViewIdSchema.optional(), requiresAuthentication: z.literal(true),
}).strict();
export const twinQueryNavigationResourceSchema = z.discriminatedUnion("type", [twinQueryChannelResourceSchema]);
const queryBase = z.object({
    id: z.string().trim().min(1),
    mode: z.enum(["session", "deterministic"]),
    interfaceType: twinQueryInterfaceSchema,
    clientApp: externalClientAppSchema.optional(),
    taskId: taskIdSchema.optional(),
    sessionId: z.string().trim().min(1).optional(),
    createdAt: z.number().int().nonnegative(),
    updatedAt: z.number().int().nonnegative(),
}).strict();
const pending = queryBase.extend({ mode: z.literal("session"), status: z.enum(["queued", "running"]), taskId: taskIdSchema }).strict();
const failed = queryBase.extend({
    mode: z.literal("session"), status: z.enum(["failed", "cancelled"]), taskId: taskIdSchema,
    error: z.object({ code: z.string().trim().min(1).max(120), message: z.string().trim().min(1).max(2_000) }).strict(),
}).strict();
const sessionCompleted = queryBase.extend({
    mode: z.literal("session"), status: z.literal("completed"), taskId: taskIdSchema,
    answer: z.string().trim().min(1), resources: z.array(twinQueryNavigationResourceSchema).max(32).default([]),
}).strict();
const deterministicCompleted = queryBase.extend({
    mode: z.literal("deterministic"), status: z.literal("completed"),
    data: z.object({
        rows: z.array(z.record(z.string(), z.unknown())).max(200),
        complete: z.boolean(),
        nextCursor: z.string().nullable(),
        freshness: channelDataFreshnessSchema,
    }).strict(),
}).strict();
export const twinQueryResourceSchema = z.union([pending, failed, sessionCompleted, deterministicCompleted]);
export const twinQueryCreateResponseSchema = z.object({ query: z.union([pending, deterministicCompleted]) }).strict();
export const twinQueryGetResponseSchema = z.object({ query: twinQueryResourceSchema }).strict();
export const twinFeedbackCreateInputSchema = z.object({
    interfaceType: twinQueryInterfaceSchema.default("api"),
    description: z.string().trim().min(1).max(8_000),
    queryId: z.string().trim().min(1).max(200).optional(),
}).strict();
export const twinFeedbackResourceSchema = z.object({
    id: z.string().trim().min(1),
    interfaceType: twinQueryInterfaceSchema,
    queryId: z.string().trim().min(1).max(200).optional(),
    createdAt: z.number().int().nonnegative(),
}).strict();
export const twinFeedbackCreateResponseSchema = z.object({
    feedback: twinFeedbackResourceSchema,
}).strict();
export const twinArtifactCreateInputSchema = z.object({
    kind: z.enum(["markdown", "csv"]),
    title: z.string().trim().min(1).max(200),
    content: z.string().min(1).max(50_000),
    filename: z.string().trim().min(1).max(120).optional(),
    channelId: channelIdSchema.optional(),
    idempotencyKey: z.string().trim().min(8).max(160)
        .regex(/^[A-Za-z0-9][A-Za-z0-9._:-]*$/u),
    interfaceType: twinQueryInterfaceSchema.default("api"),
    clientApp: externalClientAppSchema.optional(),
}).strict();
export const twinArtifactResourceSchema = z.object({
    id: z.string().trim().min(1),
    versionId: z.string().trim().min(1),
    taskId: taskIdSchema,
    runId: runIdSchema,
    sessionId: z.string().trim().min(1).optional(),
    kind: z.enum(["markdown", "csv"]),
    title: z.string().trim().min(1),
    filename: z.string().trim().min(1),
    mimeType: z.enum(["text/markdown", "text/csv"]),
    byteLength: z.number().int().positive(),
    sha256: z.string().regex(/^[a-f0-9]{64}$/u),
    channelId: channelIdSchema.optional(),
    interfaceType: twinQueryInterfaceSchema,
    clientApp: externalClientAppSchema.optional(),
    createdAt: z.number().int().nonnegative(),
}).strict();
export const twinArtifactCreateResponseSchema = z.object({
    artifact: twinArtifactResourceSchema,
    created: z.boolean(),
}).strict();
export const twinArtifactUpdateInputSchema = z.object({
    expectedVersionId: z.string().trim().min(1).max(200),
    title: z.string().trim().min(1).max(200),
    content: z.string().min(1).max(50_000),
    filename: z.string().trim().min(1).max(120).optional(),
    idempotencyKey: z.string().trim().min(8).max(160)
        .regex(/^[A-Za-z0-9][A-Za-z0-9._:-]*$/u),
    interfaceType: twinQueryInterfaceSchema.default("api"),
    clientApp: externalClientAppSchema.optional(),
}).strict();
export const twinArtifactUpdateResponseSchema = z.object({
    artifact: twinArtifactResourceSchema,
    updated: z.boolean(),
}).strict();
export const twinSessionsListResponseSchema = z.object({
    sessions: z.array(twinSessionSummarySchema),
    nextCursor: z.string().nullable(),
    hasMore: z.boolean(),
}).strict();
export const twinSessionTurnSchema = z.object({
    id: runIdSchema,
    userMessage: z.string(),
    assistantResponse: z.string().optional(),
    status: taskRunStatusSchema,
    error: z.object({
        code: z.string().optional(),
        message: z.string(),
    }).strict().optional(),
    createdAt: z.number(),
    updatedAt: z.number(),
}).strict();
export const twinSessionGetResponseSchema = z.object({
    session: twinSessionSummarySchema,
    turns: z.array(twinSessionTurnSchema),
    nextCursor: z.string().nullable(),
    hasOlder: z.boolean(),
}).strict();
export const twinReadableArtifactSummarySchema = z.object({
    id: z.string().trim().min(1),
    sessionId: z.string().trim().min(1).optional(),
    provider: artifactProviderSchema,
    kind: artifactKindSchema,
    title: z.string().trim().min(1),
    currentVersionId: z.string().trim().min(1).optional(),
    version: z.number().int().positive(),
    createdAt: z.number(),
    updatedAt: z.number(),
}).strict();
export const twinReadableArtifactsListResponseSchema = z.object({
    artifacts: z.array(twinReadableArtifactSummarySchema),
}).strict();
export const twinChannelArtifactSummarySchema = twinReadableArtifactSummarySchema.extend({
    channelId: z.string().trim().min(1),
    origins: z.array(channelArtifactOriginSchema).min(1),
    referencedSessionIds: z.array(z.string().trim().min(1)),
}).strict();
export const twinChannelArtifactsListResponseSchema = z.object({
    artifacts: z.array(twinChannelArtifactSummarySchema),
    nextCursor: z.string().trim().min(1).nullable(),
    hasMore: z.boolean(),
}).strict();
export const twinReadableArtifactGetResponseSchema = z.object({
    artifact: twinReadableArtifactSummarySchema.nullable(),
    versions: z.array(z.object({
        id: z.string().trim().min(1),
        version: z.number().int().positive(),
        title: z.string().trim().min(1),
        status: artifactVersionStatusSchema,
        createdAt: z.number(),
    }).strict()),
}).strict();
export const twinProductQueryResourceSchema = z.object({
    id: runIdSchema, taskId: taskIdSchema, sessionId: z.string().trim().min(1).optional(),
    product: twinQueryProductSchema, status: taskRunStatusSchema,
    createdAt: z.number().int().nonnegative(), updatedAt: z.number().int().nonnegative(),
    answer: z.string().trim().min(1).optional(), resources: z.array(twinQueryNavigationResourceSchema).optional(),
    evidence: z.object({ queriedRelations: z.array(z.string().trim().min(1).max(260)), certificateRefs: z.array(twinExecutionCertificateRefSchema) }).strict().optional(),
    error: z.object({ code: z.string(), message: z.string() }).strict().optional(),
}).strict();
export const twinProductQueryCreateResponseSchema = z.object({ query: twinProductQueryResourceSchema }).strict();
export const twinProductQueryGetResponseSchema = z.object({ query: twinProductQueryResourceSchema }).strict();
export const TWIN_PUBLIC_OPERATIONS = {
    ...TWIN_WORKFLOW_OPERATIONS,
    accessEffective: { operationId: "twin.access.effective", method: "POST", path: "/api/twin/access/effective", requiredScope: "twin.access.read", mcpTool: "twin_access_effective" },
    accessExplain: { operationId: "twin.access.explain", method: "POST", path: "/api/twin/access/explain", requiredScope: "twin.access.read", mcpTool: "twin_access_explain" },
    listMemories: { operationId: "listMemories", method: "GET", path: "/api/memories", requiredScope: "twin.read", mcpTool: "twin_memories_list" },
    createMemory: { operationId: "createMemory", method: "POST", path: "/api/memories", requiredScope: "twin.write", mcpTool: "twin_memory_create" },
    editMemory: { operationId: "correctMemory", method: "POST", path: "/api/memories/{memoryId}/correct", requiredScope: "twin.write", mcpTool: "twin_memory_edit" },
    memoryHistory: { operationId: "getMemoryHistory", method: "GET", path: "/api/memories/{memoryId}/history", requiredScope: "twin.read" },
    retractMemory: { operationId: "retractMemory", method: "POST", path: "/api/memories/{memoryId}/retract", requiredScope: "twin.write" },
    forgetMemory: { operationId: "forgetMemory", method: "DELETE", path: "/api/memories/{memoryId}", requiredScope: "twin.write" },
    listChannels: { operationId: "twin.channels.list", method: "GET", path: "/api/twin/channels", requiredScope: "twin.read", mcpTool: "twin_channels_list" },
    listChannelGroups: { operationId: "twin.channel-groups.list", method: "GET", path: "/api/twin/channel-groups", requiredScope: "twin.read", mcpTool: "twin_channel_groups_list" },
    createChannel: { operationId: "twin.channels.create", method: "POST", path: "/api/twin/channels", requiredScope: "twin.write", mcpTool: "twin_channel_create" },
    updateChannel: { operationId: "twin.channels.update", method: "PATCH", path: "/api/twin/channels/{channelId}", requiredScope: "twin.write", mcpTool: "twin_channel_update" },
    inspectChannel: { operationId: "twin.channels.inspect", method: "GET", path: "/api/twin/channels/{channelId}", requiredScope: "twin.read", mcpTool: "twin_channels_inspect" },
    createChannelObjectType: { operationId: "twin.channel-object-types.create", method: "POST", path: "/api/twin/channels/{channelId}/object-types", requiredScope: "twin.write", mcpTool: "twin_channel_object_type_create" },
    updateChannelObjectType: { operationId: "twin.channel-object-types.update", method: "PATCH", path: "/api/twin/channels/{channelId}/object-types/{objectTypeId}", requiredScope: "twin.write" },
    declareChannelDataFreshness: { operationId: "twin.channel-object-types.data-freshness.update", method: "PATCH", path: "/api/twin/channels/{channelId}/object-types/{objectTypeId}/data-freshness", requiredScope: "twin.write" },
    createChannelObjectField: { operationId: "twin.channel-object-fields.create", method: "POST", path: "/api/twin/channels/{channelId}/object-types/{objectTypeId}/fields", requiredScope: "twin.write", mcpTool: "twin_channel_field_create" },
    updateChannelObjectField: { operationId: "twin.channel-object-fields.update", method: "PATCH", path: "/api/twin/channels/{channelId}/object-types/{objectTypeId}/fields/{fieldKey}", requiredScope: "twin.write", mcpTool: "twin_channel_field_update" },
    listChannelMembers: { operationId: "twin.channel-members.list", method: "GET", path: "/api/twin/channels/{channelId}/members", requiredScope: "twin.read", mcpTool: "twin_channel_members_list" },
    listMembers: { operationId: "twin.members.list", method: "GET", path: "/api/twin/members", requiredScope: "twin.read", mcpTool: "twin_members_list" },
    listChannelViews: { operationId: "twin.channel-views.list", method: "GET", path: "/api/twin/channels/{channelId}/views", requiredScope: "twin.read", mcpTool: "twin_channel_views_list" },
    createChannelView: { operationId: "twin.channel-views.create", method: "POST", path: "/api/twin/channels/{channelId}/views", requiredScope: "twin.write", mcpTool: "twin_channel_view_create" },
    getChannelView: { operationId: "twin.channel-views.get", method: "GET", path: "/api/twin/channels/{channelId}/views/{viewId}", requiredScope: "twin.read", mcpTool: "twin_channel_view_get" },
    getChannelDashboardData: { operationId: "twin.channel-views.data", method: "GET", path: "/api/twin/channels/{channelId}/views/{viewId}/data", requiredScope: "twin.read", mcpTool: "twin_channel_dashboard_data" },
    updateChannelView: { operationId: "twin.channel-views.update", method: "PATCH", path: "/api/twin/channels/{channelId}/views/{viewId}", requiredScope: "twin.write", mcpTool: "twin_channel_view_update" },
    createChannelRecord: { operationId: "twin.channel-records.create", method: "POST", path: "/api/twin/channels/{channelId}/records", requiredScope: "twin.write", mcpTool: "twin_channel_record_create" },
    getChannelRecord: { operationId: "twin.channel-records.get", method: "GET", path: "/api/twin/channels/{channelId}/records/{recordId}", requiredScope: "twin.read", mcpTool: "twin_channel_record_get" },
    updateChannelRecord: { operationId: "twin.channel-records.update", method: "PATCH", path: "/api/twin/channels/{channelId}/records/{recordId}", requiredScope: "twin.write", mcpTool: "twin_channel_record_update" },
    listChannelFeed: { operationId: "twin.channel-feed.list", method: "GET", path: "/api/twin/channels/{channelId}/feed", requiredScope: "twin.read", mcpTool: "twin_channel_feed_list" },
    listChannelArtifacts: { operationId: "twin.channel-artifacts.list", method: "GET", path: "/api/twin/channels/{channelId}/artifacts", requiredScope: "twin.read", mcpTool: "twin_channel_artifacts_list" },
    createChannelPost: { operationId: "twin.channel-posts.create", method: "POST", path: "/api/twin/channels/{channelId}/posts", requiredScope: "twin.write", mcpTool: "twin_channel_post_create" },
    updateChannelPost: { operationId: "twin.channel-posts.update", method: "PATCH", path: "/api/twin/channels/{channelId}/posts/{postId}", requiredScope: "twin.write", mcpTool: "twin_channel_post_update" },
    deleteChannelPost: { operationId: "twin.channel-posts.delete", method: "DELETE", path: "/api/twin/channels/{channelId}/posts/{postId}", requiredScope: "twin.write", mcpTool: "twin_channel_post_delete" },
    listChannelPostReplies: { operationId: "twin.channel-post-replies.list", method: "GET", path: "/api/twin/channels/{channelId}/posts/{postId}/replies", requiredScope: "twin.read", mcpTool: "twin_channel_post_replies_list" },
    createChannelPostReply: { operationId: "twin.channel-post-replies.create", method: "POST", path: "/api/twin/channels/{channelId}/posts/{postId}/replies", requiredScope: "twin.write", mcpTool: "twin_channel_post_reply_create" },
    getChannelFeedThread: { operationId: "twin.channel-feed-threads.get", method: "GET", path: "/api/twin/channels/{channelId}/feed/threads/{subjectType}/{subjectId}", requiredScope: "twin.read", mcpTool: "twin_channel_feed_thread_get" },
    createChannelFeedComment: { operationId: "twin.channel-feed-comments.create", method: "POST", path: "/api/twin/channels/{channelId}/feed/threads/{subjectType}/{subjectId}/comments", requiredScope: "twin.write", mcpTool: "twin_channel_feed_comment_create" },
    updateChannelFeedComment: { operationId: "twin.channel-feed-comments.update", method: "PATCH", path: "/api/twin/channels/{channelId}/feed/comments/{commentId}", requiredScope: "twin.write", mcpTool: "twin_channel_feed_comment_update" },
    deleteChannelFeedComment: { operationId: "twin.channel-feed-comments.delete", method: "DELETE", path: "/api/twin/channels/{channelId}/feed/comments/{commentId}", requiredScope: "twin.write", mcpTool: "twin_channel_feed_comment_delete" },
    setChannelFeedReaction: { operationId: "twin.channel-feed-reactions.set", method: "POST", path: "/api/twin/channels/{channelId}/feed/threads/{subjectType}/{subjectId}/reactions", requiredScope: "twin.write", mcpTool: "twin_channel_feed_reaction_set" },
    listFiles: { operationId: "twin.files.list", method: "GET", path: "/api/twin/files", requiredScope: "twin.read", mcpTool: "twin_files_list" },
    uploadFile: { operationId: "twin.files.upload", method: "POST", path: "/api/twin/files", requiredScope: "twin.write" },
    uploadInlineFile: { operationId: "twin.files.upload-inline", method: "POST", path: "/api/twin/files/inline", requiredScope: "twin.write", mcpTool: "twin_file_upload" },
    prepareFileUpload: { operationId: "twin.files.upload-prepare", method: "POST", path: "/api/twin/files/upload-prepare", requiredScope: "twin.write", mcpTool: "twin_file_upload_prepare" },
    getFile: { operationId: "twin.files.get", method: "GET", path: "/api/twin/files/{fileId}", requiredScope: "twin.read", mcpTool: "twin_file_get" },
    getFileContent: { operationId: "twin.files.content", method: "GET", path: "/api/twin/files/{fileId}/content", requiredScope: "twin.read", mcpTool: "twin_file_content" },
    attachFile: { operationId: "twin.files.attach", method: "POST", path: "/api/twin/files/{fileId}/links", requiredScope: "twin.write", mcpTool: "twin_file_attach" },
    detachFile: { operationId: "twin.files.detach", method: "DELETE", path: "/api/twin/files/{fileId}/links", requiredScope: "twin.write", mcpTool: "twin_file_detach" },
    listSessions: { operationId: "twin.sessions.list", method: "GET", path: "/api/twin/sessions", requiredScope: "twin.read", mcpTool: "twin_sessions_list" },
    getSession: { operationId: "twin.sessions.get", method: "GET", path: "/api/twin/sessions/{sessionId}", requiredScope: "twin.read", mcpTool: "twin_session_get" },
    listSessionArtifacts: { operationId: "twin.session-artifacts.list", method: "GET", path: "/api/twin/sessions/{sessionId}/artifacts", requiredScope: "twin.read", mcpTool: "twin_session_artifacts_list" },
    getArtifact: { operationId: "twin.artifacts.get", method: "GET", path: "/api/twin/artifacts/{artifactId}", requiredScope: "twin.read", mcpTool: "twin_artifact_get" },
    getArtifactContent: { operationId: "twin.artifacts.content", method: "GET", path: "/api/twin/artifacts/{artifactId}/content", requiredScope: "twin.read", mcpTool: "twin_artifact_content" },
    attachSession: { operationId: "twin.sessions.attach", method: "POST", path: "/api/twin/sessions/{sessionId}/channels", requiredScope: "twin.write", mcpTool: "twin_session_attach" },
    detachSession: { operationId: "twin.sessions.detach", method: "DELETE", path: "/api/twin/sessions/{sessionId}/channels", requiredScope: "twin.write", mcpTool: "twin_session_detach" },
    attachArtifact: { operationId: "twin.artifacts.attach", method: "POST", path: "/api/twin/artifacts/{artifactId}/links", requiredScope: "twin.write", mcpTool: "twin_artifact_attach" },
    detachArtifact: { operationId: "twin.artifacts.detach", method: "DELETE", path: "/api/twin/artifacts/{artifactId}/links", requiredScope: "twin.write", mcpTool: "twin_artifact_detach" },
    createArtifact: { operationId: "twin.artifacts.create", method: "POST", path: "/api/twin/artifacts", requiredScope: "twin.write", mcpTool: "twin_artifact_create" },
    updateArtifact: { operationId: "twin.artifacts.update", method: "PATCH", path: "/api/twin/artifacts/{artifactId}", requiredScope: "twin.write", mcpTool: "twin_artifact_update" },
    createQuery: { operationId: "twin.query.create", method: "POST", path: "/api/twin/queries", requiredScope: "twin.query", mcpTool: "twin_query" },
    getQuery: { operationId: "twin.query.get", method: "GET", path: "/api/twin/queries/{queryId}", requiredScope: "twin.read", mcpTool: "twin_query_result" },
    createFeedback: { operationId: "twin.feedback.create", method: "POST", path: "/api/twin/feedback", requiredScope: "twin.query", mcpTool: "twin_feedback" },
    listFeed: { operationId: "twin.feed.list", method: "GET", path: "/api/twin/feed", requiredScope: "twin.read", mcpTool: "twin_feed_list" },
    markFeedRead: { operationId: "twin.feed.mark-read", method: "POST", path: "/api/twin/feed/read-cursor", requiredScope: "twin.write", mcpTool: "twin_feed_mark_read" },
    setFeedItemState: { operationId: "twin.feed.item-state", method: "POST", path: "/api/twin/feed/item-state", requiredScope: "twin.write", mcpTool: "twin_feed_item_state" },
    expandFeed: { operationId: "twin.feed.expand", method: "POST", path: "/api/twin/feed/expand", requiredScope: "twin.read", mcpTool: "twin_feed_expand" },
    listNotifications: { operationId: "twin.notifications.list", method: "GET", path: "/api/workspace/notifications", requiredScope: "twin.read", mcpTool: "twin_notifications_list" },
    updateNotification: { operationId: "twin.notifications.update", method: "PATCH", path: "/api/workspace/notifications/{notificationId}", requiredScope: "twin.write", mcpTool: "twin_notification_update" },
    getNotificationCapabilities: { operationId: "twin.notifications.capabilities", method: "GET", path: "/api/workspace/notifications/capabilities", requiredScope: "twin.read", mcpTool: "twin_notification_capabilities" },
    getNotificationPreferences: { operationId: "twin.notifications.preferences.list", method: "GET", path: "/api/workspace/notifications/preferences", requiredScope: "twin.read", mcpTool: "twin_notification_preferences_list" },
    updateNotificationPreference: { operationId: "twin.notifications.preferences.update", method: "PATCH", path: "/api/workspace/notifications/preferences", requiredScope: "twin.write", mcpTool: "twin_notification_preference_update" },
    listNotificationWatches: { operationId: "twin.notifications.watches.list", method: "GET", path: "/api/workspace/notifications/watches", requiredScope: "twin.read", mcpTool: "twin_notification_watches_list" },
    createNotificationWatch: { operationId: "twin.notifications.watches.create", method: "POST", path: "/api/workspace/notifications/watches", requiredScope: "twin.write", mcpTool: "twin_notification_watch_create" },
    deleteNotificationWatch: { operationId: "twin.notifications.watches.delete", method: "DELETE", path: "/api/workspace/notifications/watches", requiredScope: "twin.write", mcpTool: "twin_notification_watch_delete" },
    listMatchers: { operationId: "twin.matching.matchers.list", method: "GET", path: "/api/twin/matching/matchers", requiredScope: "twin.read" },
    upsertMatcher: { operationId: "twin.matching.matchers.upsert", method: "POST", path: "/api/twin/matching/matchers", requiredScope: "twin.write" },
    retireMatcher: { operationId: "twin.matching.matchers.retire", method: "POST", path: "/api/twin/matching/matchers/{matcherId}/retire", requiredScope: "twin.write" },
    getMatcherReviewers: { operationId: "twin.matching.matchers.reviewers.get", method: "GET", path: "/api/twin/matching/matchers/{matcherSlug}/reviewers", requiredScope: "twin.read" },
    setMatcherReviewers: { operationId: "twin.matching.matchers.reviewers.set", method: "PUT", path: "/api/twin/matching/matchers/{matcherSlug}/reviewers", requiredScope: "twin.write" },
    listIdentityLinks: { operationId: "twin.matching.identity-links.list", method: "GET", path: "/api/twin/matching/identity-links", requiredScope: "twin.read" },
    listTwinProposals: { operationId: "twin.proposals.list", method: "GET", path: "/api/twin/proposals", requiredScope: "twin.read" },
    bulkTwinProposals: { operationId: "twin.proposals.bulk", method: "POST", path: "/api/twin/proposals/bulk", requiredScope: "twin.write" },
    getTwinProposal: { operationId: "twin.proposals.get", method: "GET", path: "/api/twin/proposals/{proposalId}", requiredScope: "twin.read" },
    decideTwinProposal: { operationId: "twin.proposals.decide", method: "POST", path: "/api/twin/proposals/{proposalId}/decision", requiredScope: "twin.write" },
    undoTwinProposal: { operationId: "twin.proposals.undo", method: "POST", path: "/api/twin/proposals/{proposalId}/undo", requiredScope: "twin.write" },
    retireTwinProposal: { operationId: "twin.proposals.retire", method: "POST", path: "/api/twin/proposals/{proposalId}/retire", requiredScope: "twin.write" },
    listTwinBatches: { operationId: "twin.batches.list", method: "GET", path: "/api/twin/batches", requiredScope: "twin.read" },
    runTwinBatch: { operationId: "twin.batches.run", method: "POST", path: "/api/twin/batches", requiredScope: "twin.write" },
    getTwinBatch: { operationId: "twin.batches.get", method: "GET", path: "/api/twin/batches/{batchId}", requiredScope: "twin.read" },
    acceptTwinBatch: { operationId: "twin.batches.accept", method: "POST", path: "/api/twin/batches/{batchId}/accept", requiredScope: "twin.write" },
    listTwinChanges: { operationId: "twin.changes.list", method: "GET", path: "/api/twin/changes", requiredScope: "twin.read" },
    getTwinPolicies: { operationId: "twin.policies.get", method: "GET", path: "/api/twin/policies", requiredScope: "twin.read" },
    updateTwinPolicy: { operationId: "twin.policies.update", method: "PUT", path: "/api/twin/policies", requiredScope: "twin.write" },
    listAutomations: { operationId: "twin.automations.list", method: "GET", path: "/api/twin/automations", requiredScope: "twin.read", mcpTool: "twin_automations_list" },
    createAutomation: { operationId: "twin.automations.create", method: "POST", path: "/api/twin/automations", requiredScope: "twin.write", mcpTool: "twin_automation_create" },
    updateAutomation: { operationId: "twin.automations.update", method: "PATCH", path: "/api/twin/automations/{automationId}", requiredScope: "twin.write", mcpTool: "twin_automation_update" },
    deleteAutomation: { operationId: "twin.automations.delete", method: "DELETE", path: "/api/twin/automations/{automationId}", requiredScope: "twin.write", mcpTool: "twin_automation_delete" },
    runAutomation: { operationId: "twin.automations.run", method: "POST", path: "/api/twin/automations/{automationId}/run-now", requiredScope: "twin.write", mcpTool: "twin_automation_run" },
    listAutomationRuns: { operationId: "twin.automation-runs.list", method: "GET", path: "/api/twin/automations/{automationId}/runs", requiredScope: "twin.read", mcpTool: "twin_automation_runs_list" },
    listConnections: { operationId: "twin.connections.list", method: "GET", path: "/api/twin/connections", requiredScope: "twin.connections", mcpTool: "twin_connections_list" },
    verifyConnection: { operationId: "twin.connections.verify", method: "POST", path: "/api/twin/connections/verify", requiredScope: "twin.connections", mcpTool: "twin_connections_verify" },
    planOnboarding: { operationId: "twin.onboarding.plan", method: "POST", path: "/api/twin/onboarding/plan", requiredScope: "twin.onboarding", mcpTool: "twin_onboarding_plan" },
    createOnboarding: { operationId: "twin.onboarding.create", method: "POST", path: "/api/twin/onboarding/journeys", requiredScope: "twin.onboarding", mcpTool: "twin_onboarding_start" },
    listOnboarding: { operationId: "twin.onboarding.list", method: "GET", path: "/api/twin/onboarding/journeys", requiredScope: "twin.onboarding", mcpTool: "twin_onboarding_list" },
    getOnboarding: { operationId: "twin.onboarding.get", method: "GET", path: "/api/twin/onboarding/journeys/{journeyId}", requiredScope: "twin.onboarding", mcpTool: "twin_onboarding_status" },
    continueOnboarding: { operationId: "twin.onboarding.continue", method: "POST", path: "/api/twin/onboarding/journeys/{journeyId}/continue", requiredScope: "twin.onboarding", mcpTool: "twin_onboarding_continue" },
    createAuthoringPreparation: { operationId: "createTwinAuthoringPreparation", method: "POST", path: "/api/twin/authoring-preparations", requiredScope: "twin.write" },
    listAuthoringPreparations: { operationId: "listTwinAuthoringPreparations", method: "GET", path: "/api/twin/authoring-preparations", requiredScope: "twin.read" },
    getAuthoringPreparation: { operationId: "getTwinAuthoringPreparation", method: "GET", path: "/api/twin/authoring-preparations/{preparationId}", requiredScope: "twin.read" },
    cancelAuthoringPreparation: { operationId: "cancelTwinAuthoringPreparation", method: "POST", path: "/api/twin/authoring-preparations/{preparationId}/cancel", requiredScope: "twin.write" },
    listAuthoringRuns: { operationId: "listTwinAuthoringRuns", method: "GET", path: "/api/twin/authoring-runs", requiredScope: "twin.read" },
    getAuthoringRun: { operationId: "getTwinAuthoringRun", method: "GET", path: "/api/twin/authoring-runs/{runId}", requiredScope: "twin.read" },
    cancelAuthoringRun: { operationId: "cancelTwinAuthoringRun", method: "POST", path: "/api/twin/authoring-runs/{runId}/cancel", requiredScope: "twin.write" },
    listAuthoringArtifacts: { operationId: "listTwinAuthoringArtifacts", method: "GET", path: "/api/twin/authoring-runs/{runId}/artifacts", requiredScope: "twin.read" },
    getAuthoringArtifactContent: { operationId: "getTwinAuthoringArtifactContent", method: "GET", path: "/api/twin/authoring-runs/{runId}/artifacts/{artifactId}", requiredScope: "twin.read" },
    listLogicProducts: { operationId: "listTwinLogicProducts", method: "GET", path: "/api/twin/logic/products", requiredScope: "twin.read" },
};
export const taskRunStatusForTwinQuerySchema = taskRunStatusSchema;
